import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import type { ParagraphGapSelectContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Choose the sentence that best fits each gap in a passage (mirrors iOS
 *  ParagraphGapSelectView). Answer is a per-gap map of chosen candidate labels.
 *  The passage comes from the bulk-level shared passage when present, else the
 *  item's own `passage`. */
export function ParagraphGapSelectRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  sharedPassageText,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as ParagraphGapSelectContent;
  const map = (answer as Record<string, string>) ?? {};
  const passage = sharedPassageText && sharedPassageText.trim() ? sharedPassageText : c.passage;

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      {passage && (
        <div className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-ink">{passage}</div>
      )}
      <div className="mt-4 space-y-4">
        {(c.gaps ?? []).map((g) => {
          const chosen = map[String(g.index)];
          return (
            <div key={g.index} className="rounded-xl border border-black/10 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                {t("Gap {{n}}", { n: g.index + 1 })}
              </div>
              <div className="space-y-1.5">
                {g.candidates.map((cand) => {
                  let tone = "border-black/10 bg-white hover:border-brand-300";
                  if (revealed) {
                    if (cand.label === g.correct_label) tone = "border-emerald-400 bg-emerald-50";
                    else if (cand.label === chosen) tone = "border-red-400 bg-red-50";
                    else tone = "border-black/10 bg-white opacity-60";
                  } else if (cand.label === chosen) tone = "border-brand-500 bg-brand-50 ring-1 ring-brand-300";
                  return (
                    <button
                      key={cand.label}
                      type="button"
                      disabled={revealed}
                      onClick={() => setAnswer({ ...map, [String(g.index)]: cand.label })}
                      className={cx("w-full rounded-lg border px-3 py-2 text-left text-sm transition disabled:cursor-default", tone)}
                    >
                      {cand.sentence}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
